import { HttpClient } from '@angular/common/http';
import { ApplicationRef, Injectable, OnDestroy } from '@angular/core';
import { CurrencyCode, Localisation, RegionVariant, TimeFormat } from 'app/model/enums/region-variant';
import {
  BehaviorSubject,
  filter,
  first,
  map,
  mergeMap,
  Observable,
  of,
  ReplaySubject,
  shareReplay,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { RegionVariantDetails, regionVariants } from './region-variant';
import { Settings } from 'luxon';
import { UserService } from '../user/user.service';
import { User } from '../user/user.types';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class RegionVariantService implements OnDestroy {
  private readonly fallbackRegionVariant: RegionVariant = RegionVariant.Australia;

  private readonly availableRegionVariants: Set<string> = new Set<string>(Object.values(RegionVariant));
  private readonly availableCurrencyCodes: Set<string> = new Set<string>(Object.values(CurrencyCode));
  private readonly availableLocalisations: Set<string> = new Set<string>(Object.values(Localisation));

  private readonly currencyReplacementTag: string = '{{currency}}';

  private regionVariant: RegionVariant;
  private regionVariantDetails: RegionVariantDetails;
  private currencyCode: CurrencyCode;
  private currencySymbol: string;
  private currencySymbolIcon: string;
  private currencySymbolCircleIcon: string;
  private localisation: Localisation;
  private timeEntryMethodOverride: 'decimal' | 'minutes' | 'conventional' | null = null;

  readonly changes$: ReplaySubject<void> = new ReplaySubject(1);
  readonly unsubscribeAll$: Subject<void> = new Subject();

  constructor(private userService: UserService) {
    this.initDefaultRegionVariant();
    this.setupWindowTestHooks();
    this.userService.user$
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((user: User) => this.updateRegionVariantFromUser(user));
  }

  /**
   * Expose functions on window so Cypress e2e tests can force the region variant and time entry
   * units, independent of the logged-in user's own settings.
   */
  private setupWindowTestHooks(): void {
    const win = window as any;
    win.setRegionVariant = (variant: RegionVariant) => this.setRegionVariant(variant);
    win.getRegionVariant = () => this.getRegionVariant();
    win.setTimeEntryMethodOverride = (method: 'decimal' | 'minutes' | 'conventional') =>
      this.setTimeEntryMethodOverride(method);
  }
  ngOnDestroy(): void {
    this.changes$.complete();
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  private initDefaultRegionVariant(): void {
    let defaultRegionVariant = environment.defaultRegionVariant as RegionVariant;
    if (!this.availableRegionVariants.has(defaultRegionVariant)) {
      defaultRegionVariant = this.fallbackRegionVariant;
    }
    this.setRegionVariant(defaultRegionVariant);
  }

  private updateRegionVariantFromUser(user: User) {
    const variant = user.settings.variant;

    // Ensure default is always set
    this.initDefaultRegionVariant();

    if (variant) {
      if (variant.region) this.setRegionVariant(variant.region);
      // TODO: Allow setting currency code, localisation etc. separately
      // Do this synchronously with the private methods so that changes$ triggers updates afterwards
    }
  }

  setRegionVariant(variant: RegionVariant): void {
    if (this.availableRegionVariants.has(variant)) {
      this.regionVariant = variant;
      this.regionVariantDetails = regionVariants[this.regionVariant];
      this._setCurrencyCode(this.regionVariantDetails.defaultCurrencyCode);
      this._setLocalisation(this.regionVariantDetails.defaultLocalisation);
    }

    this.mapCurrencySymbols();
    this.changes$.next();
  }

  setCurrencyCode(code: CurrencyCode): void {
    this._setCurrencyCode(code);
    this.changes$.next();
  }
  private _setCurrencyCode(code: CurrencyCode): void {
    if (this.availableCurrencyCodes.has(code)) {
      this.currencyCode = code;
      this.mapCurrencySymbols();
    }
  }

  setLocalisation(locale: Localisation): void {
    this._setLocalisation(locale);
    this.changes$.next();
  }
  private _setLocalisation(locale: Localisation): void {
    if (this.availableLocalisations.has(locale)) {
      this.localisation = locale;
      // locale for Luxon DateTime
      Settings.defaultLocale = locale;
    }
  }

  /** Force the time entry units (decimal/minutes/conventional), overriding the region and user defaults. For Cypress e2e tests. */
  setTimeEntryMethodOverride(method: 'decimal' | 'minutes' | 'conventional'): void {
    this.timeEntryMethodOverride = method;
    this.changes$.next();
  }
  getTimeEntryMethodOverride(): 'decimal' | 'minutes' | 'conventional' | null {
    return this.timeEntryMethodOverride;
  }

  private mapCurrencySymbols(): void {
    let symbol: string = null;
    let icon: string = null;
    let circleIcon: string = null;

    switch (this.currencyCode) {
      default:
        // TS error if list isn't exhaustive, in runtime will fall through to dollars
        const unhandledCase: never = this.currencyCode;

      case CurrencyCode.AUD:
        symbol = '$';
        icon = 'attach_money';
        circleIcon = 'heroicons_outline:currency-dollar';
        break;
      case CurrencyCode.GBP:
        symbol = '£';
        icon = 'currency_pound';
        circleIcon = 'heroicons_outline:currency-pound';
        break;
    }

    this.currencySymbol = symbol;
    this.currencySymbolIcon = icon;
    this.currencySymbolCircleIcon = circleIcon;
  }

  getLocalisation(): Localisation {
    return this.localisation;
  }
  getCurrencyCode(): CurrencyCode {
    return this.currencyCode;
  }
  getRegionVariant(): RegionVariant {
    return this.regionVariant;
  }
  isRegionVariant(variant: RegionVariant): boolean {
    return variant === this.regionVariant;
  }
  getCurrencySymbol(): string {
    return this.currencySymbol;
  }
  getCurrencySymbolIcon(): string {
    return this.currencySymbolIcon;
  }
  getCurrencySymbolCircleIcon(): string {
    return this.currencySymbolCircleIcon;
  }

  getRegionTax(): string {
    return this.regionVariantDetails.regionTax;
  }

  getNgxTimepickerFormat(): number {
    switch (this.regionVariantDetails.timeFormat) {
      default:
        // This should never occur, fall through to 12 hour time format
        const unhandledCase: never = this.regionVariantDetails.timeFormat;

      case TimeFormat.TwelveHour:
        return 12;
      case TimeFormat.TwentyFourHour:
        return 24;
    }
  }
  isTimeFormat(timeFormat: TimeFormat): boolean {
    return this.regionVariantDetails.timeFormat === timeFormat;
  }

  // TODO: Replace this with an enum
  getDefaultTimeEntryMethod(): 'decimal' | 'minutes' | 'conventional' {
    return this.regionVariantDetails.defaultTimeEntryMethod || 'decimal';
  }

  replaceCurrencySymbol(text: string): string {
    return text?.replaceAll(this.currencyReplacementTag, this.currencySymbol);
  }

  text(path: string): string {
    const result = this.stringFromPath(this.regionVariantDetails.text, path);
    return this.replaceCurrencySymbol(result);
  }

  show(path: string): boolean {
    return this.boolFromPath(this.regionVariantDetails.show, path);
  }

  flag(path: string): boolean {
    return this.boolFromPath(this.regionVariantDetails.flag, path);
  }

  timeFormattingForceHours(path: string): boolean {
    return this.boolFromPath(this.regionVariantDetails.timeFormattingForceHours, path);
  }

  private stringFromPath(obj: any, path: string): string {
    // Arrays not supported, not needed for region variant js obj
    return path.split('.').reduce((acc, p) => acc?.[p], obj) ?? path;
  }
  private boolFromPath(obj: any, path: string): boolean {
    return !!path.split('.').reduce((acc, p) => acc?.[p], obj);
  }
}
